import { LogIn, Smartphone, UserCheck, UserPlus } from 'lucide-react';

import { QueryError } from '@/components/ui/QueryError';
import { Skeleton } from '@/components/ui/Skeleton';
import { activityApi } from '@/features/activity/api/activity.api';
import { useApiQuery } from '@/hooks/useApiQuery';
import { formatNumber } from '@/lib/format';
import { CountUp } from './CountUp';

export function AdoptionStrip() {
  const { data, isLoading, error, refetch } = useApiQuery(() => activityApi.adoption(), []);

  if (error) {
    return <QueryError title="Could not load adoption totals" error={error} onRetry={refetch} />;
  }

  const accounts = data?.accounts.total ?? 0;
  const signedIn = data?.signedIn.people ?? 0;
  const appUsers = data?.signedIn.app ?? 0;
  const totalLogins = data?.logins.total ?? 0;

  return (
    <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
      {/* Card 1: Accounts created */}
      <div className="flex flex-col justify-between rounded-2xl border border-purple-100/90 dark:border-purple-900/50 bg-gradient-to-b from-white to-[#faf5ff] dark:from-slate-900 dark:to-purple-950/20 p-4 shadow-[0_4px_14px_rgba(147,51,234,0.06)] transition-all hover:-translate-y-0.5 hover:shadow-md">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[13.5px] font-semibold text-slate-600 dark:text-slate-300">Accounts created</p>
            {isLoading ? (
              <Skeleton className="mt-1.5 h-8 w-16" />
            ) : (
              <p className="mt-0.5 text-[28px] font-black text-slate-900 dark:text-white tracking-tight leading-none">
                <CountUp value={accounts} />
              </p>
            )}
          </div>
          <span className="grid h-9.5 w-9.5 shrink-0 place-items-center rounded-xl bg-purple-100/80 text-purple-700 dark:bg-purple-950/60 dark:text-purple-400 border border-purple-200/60 dark:border-purple-800 shadow-2xs">
            <UserPlus className="h-5 w-5" />
          </span>
        </div>
        <p className="mt-2 text-[11.5px] text-slate-400 dark:text-slate-500 font-medium">
          {data
            ? `Students ${formatNumber(data.accounts.byGroup.students)} · Teachers ${formatNumber(data.accounts.byGroup.teachers)} · Staff ${formatNumber(data.accounts.byGroup.staff)} · Parents ${formatNumber(data.accounts.byGroup.parents)}`
            : 'Loading accounts split...'}
        </p>
      </div>

      {/* Card 2: Ever logged in */}
      <div className="flex flex-col justify-between rounded-2xl border border-blue-100/90 dark:border-blue-900/50 bg-gradient-to-b from-white to-[#eff6ff] dark:from-slate-900 dark:to-blue-950/20 p-4 shadow-[0_4px_14px_rgba(59,130,246,0.06)] transition-all hover:-translate-y-0.5 hover:shadow-md">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[13.5px] font-semibold text-slate-600 dark:text-slate-300">Ever logged in</p>
            {isLoading ? (
              <Skeleton className="mt-1.5 h-8 w-16" />
            ) : (
              <p className="mt-0.5 text-[28px] font-black text-blue-600 dark:text-blue-400 tracking-tight leading-none">
                <CountUp value={signedIn} />
              </p>
            )}
          </div>
          <span className="grid h-9.5 w-9.5 shrink-0 place-items-center rounded-xl bg-blue-100/80 text-blue-700 dark:bg-blue-950/60 dark:text-blue-400 border border-blue-200/60 dark:border-blue-800 shadow-2xs">
            <UserCheck className="h-5 w-5" />
          </span>
        </div>
        <p className="mt-2 text-[11.5px] text-slate-400 dark:text-slate-500 font-medium">
          {data ? `of ${formatNumber(accounts)} accounts` : 'Loading...'}
        </p>
      </div>

      {/* Card 3: App users */}
      <div className="flex flex-col justify-between rounded-2xl border border-emerald-100/90 dark:border-emerald-900/50 bg-gradient-to-b from-white to-[#ecfdf5] dark:from-slate-900 dark:to-emerald-950/20 p-4 shadow-[0_4px_14px_rgba(16,185,129,0.06)] transition-all hover:-translate-y-0.5 hover:shadow-md">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[13.5px] font-semibold text-slate-600 dark:text-slate-300">App users</p>
            {isLoading ? (
              <Skeleton className="mt-1.5 h-8 w-16" />
            ) : (
              <p className="mt-0.5 text-[28px] font-black text-slate-900 dark:text-white tracking-tight leading-none">
                <CountUp value={appUsers} />
              </p>
            )}
          </div>
          <span className="grid h-9.5 w-9.5 shrink-0 place-items-center rounded-xl bg-emerald-100/80 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400 border border-emerald-200/60 dark:border-emerald-800 shadow-2xs">
            <Smartphone className="h-5 w-5" />
          </span>
        </div>
        <p className="mt-2 text-[11.5px] text-slate-400 dark:text-slate-500 font-medium truncate" title="People who have signed in through the mobile app at least once. Not a count of downloads.">
          Signed in through the mobile app at least once
        </p>
      </div>

      {/* Card 4: Total logins */}
      <div className="flex flex-col justify-between rounded-2xl border border-indigo-100/90 dark:border-indigo-900/50 bg-gradient-to-b from-white to-[#eef2ff] dark:from-slate-900 dark:to-indigo-950/20 p-4 shadow-[0_4px_14px_rgba(91,82,249,0.06)] transition-all hover:-translate-y-0.5 hover:shadow-md">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="text-[13.5px] font-semibold text-slate-600 dark:text-slate-300">Total logins</p>
            {isLoading ? (
              <Skeleton className="mt-1.5 h-8 w-16" />
            ) : (
              <p className="mt-0.5 text-[28px] font-black text-slate-900 dark:text-white tracking-tight leading-none">
                <CountUp value={totalLogins} />
              </p>
            )}
          </div>
          <span className="grid h-9.5 w-9.5 shrink-0 place-items-center rounded-xl bg-indigo-100/80 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-400 border border-indigo-200/60 dark:border-indigo-800 shadow-2xs">
            <LogIn className="h-5 w-5" />
          </span>
        </div>
        <p className="mt-2 text-[11.5px] text-slate-400 dark:text-slate-500 font-medium">
          {data
            ? `${formatNumber(data.logins.web)} Web · ${formatNumber(data.logins.app)} App · ${formatNumber(data.logins.other)} Other`
            : 'Loading logins...'}
        </p>
      </div>
    </div>
  );
}
